/**
 * @file client/src/app/(protected)/my-dogs/page.tsx
 * @description My Dogs page
 *              Lists the user's dogs with photo, basic info, and actions
 *              (view, delete). Loads dogs after auth is confirmed.
 */

"use client";

import { deleteDog, getAllDogs, getSelectedDog } from "@/api/dogApi";
import LoadingSpinner from "@/app/loading";
import { useAppContext } from "@/context/Context";
import Image from "next/image";
import { useEffect } from "react";
import Button from "@/components/Button";
import { MdAddCircleOutline } from "react-icons/md";
import { RiArrowRightSLine, RiDeleteBin7Line } from "react-icons/ri";
import { FaDog } from "react-icons/fa";

export default function MyDogsPage() {
  const { dogState, dogDispatch, userState, authLoading } = useAppContext();
  const { isLoading, error, dogs } = dogState;

  // Load dogs only when user is authenticated and auth check is complete
  useEffect(() => {
    if (!authLoading && userState.isAuthenticated) {
      getAllDogs(dogDispatch);
    }
  }, [dogDispatch, authLoading, userState.isAuthenticated]);

  if (authLoading || isLoading) {
    return (
      <div className="max-w-5xl p-4">
        <div className="text-left">
          <div className="flex flex-wrap gap-2">
            <h2 className="text-lg md:text-xl text-primary font-semibold mb-2">
              Loading your dogs...
            </h2>
            <LoadingSpinner />
          </div>
          <p className="text-sm text-foreground/70 mb-4">
            If this takes too long, try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-left">
        <h2 className="text-lg md:text-xl text-foreground font-semibold mb-4">
          We cannot show your dogs at the moment. Try refreshing the page.
        </h2>
        <p className="text-accent">{error}</p>
      </div>
    );
  }

  const handleDeleteDog = async (dogId: string, dogName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${dogName}? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteDog(dogDispatch, dogId);
      // Dog will be automatically removed from state by the reducer
    } catch (error) {
      console.error("Failed to delete dog:", error);
      // Error is already handled by the API function and displayed in state
    }
  };

  const handleViewDog = async (dogId: string) => {
    await getSelectedDog(dogDispatch, dogId);
  };

  return (
    <div className="max-w-5xl">
      <div className="">
        {/* Header */}
        <div className="flex flex-wrap justify-between gap-4 mb-8 lg:mb-16">
          <h1 className="text-lg md:text-2xl font-bold">My Dogs</h1>
          <Button
            href="/my-dogs/add-dog"
            variant="primary"
            icon={<MdAddCircleOutline className="w-5 h-5" aria-hidden="true" />}
          >
            Add Dog
          </Button>
        </div>

        {/* Dogs Grid */}
        <div className="grid grid-cols-1 gap-4 lg:gap-6">
          {Array.isArray(dogs) && dogs.length > 0 ? (
            dogs
              .filter((dog) => dog && dog.id) // Filter out undefined/null dogs
              .map((dog) => (
                <div
                  key={dog.id}
                  className="bg-main-text-bg rounded-lg p-4 border border-primary/20 hover:border-primary/40 transition-colors last:mb-28 lg:mb-0"
                >
                  <div className="flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-6 md:items-center">
                    {/*  Image and Info */}
                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* Dog Image */}
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full border-2 border-primary/30 overflow-hidden bg-primary/10">
                          {dog.photo?.url ? (
                            <Image
                              src={dog.photo.url}
                              alt={`${dog.name || "Dog"} photo`}
                              width={80}
                              height={80}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/10">
                              <FaDog
                                className="w-8 h-8 text-primary/50"
                                aria-hidden="true"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Dog Info */}
                      <div className="flex-1 text-left">
                        <h2 className="text-lg font-semibold text-foreground mb-1">
                          {dog.name
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ") || "Unnamed Dog"}
                        </h2>

                        <div className="text-sm md:text-base text-foreground/70">
                          {dog.age && <p>Age: {dog.age}</p>}
                          {dog.breed && <p>Breed: {dog.breed}</p>}
                          <p>Max BPM: {dog.maxBreathingRate || "Not set"} </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between md:justify-end gap-2 sm:gap-3 md:gap-3 mt-2 md:mt-0">
                      <Button
                        href={`/my-dogs/${dog.id}`}
                        variant="secondary"
                        size="sm"
                        icon={
                          <RiArrowRightSLine
                            className="w-4 h-4 md:w-5 md:h-5"
                            aria-hidden="true"
                          />
                        }
                        iconPosition="right"
                        className="shadow-md text-sm md:text-sm flex-1 md:flex-none"
                        onClick={() => handleViewDog(dog.id)} // Prevent card click
                      >
                        View Dog
                      </Button>

                      <Button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDeleteDog(dog.id, dog.name);
                        }}
                        ariaLabel="Delete dog"
                        title="Delete dog"
                        variant="dangerGhost"
                        size="sm"
                        icon={
                          <RiDeleteBin7Line
                            className="w-4 h-4 md:w-4 md:h-4"
                            aria-hidden="true"
                          />
                        }
                        iconOnly
                        shape="square"
                      />
                    </div>
                  </div>
                </div>
              ))
          ) : (
            <div className="col-span-full text-left py-6">
              <h2 className="text-lg md:text-xl text-foreground font-semibold mb-4">
                No dogs found
              </h2>
              <p className="text-foreground/70 leading-snug mb-6">
                You haven`t added any dogs yet. Start by adding your first dog!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
